import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Toast, ToastVariant } from './Toast';
import { ShowToastOptions, ToastAction, ToastContext, ToastContextValue } from './ToastContext';
import styles from './Toast.module.scss';

const DEFAULT_DURATION_MS = 4000;

interface ToastEntry {
  id: number;
  message: string;
  variant: ToastVariant;
  actions?: ToastAction[];
}

interface Props {
  children: React.ReactNode;
}

export function ToastProvider({ children }: Props) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const [mounted, setMounted] = useState(false);
  const idRef = useRef(0);
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    setMounted(true);
    const timers = timersRef.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  const dismiss = useCallback((id: number) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((entry) => entry.id !== id));
  }, []);

  const showToast = useCallback(
    ({ message, variant = 'success', durationMs = DEFAULT_DURATION_MS, actions }: ShowToastOptions) => {
      idRef.current += 1;
      const id = idRef.current;
      setToasts((prev) => [...prev, { id, message, variant, actions }]);
      // Errors must persist until dismissed (WCAG 2.2.1) — only auto-dismiss
      // success toasts. Toasts with actions also persist so the user can act.
      const hasActions = !!actions && actions.length > 0;
      if (variant !== 'error' && !hasActions) {
        const timer = setTimeout(() => dismiss(id), durationMs);
        timersRef.current.set(id, timer);
      }
    },
    [dismiss],
  );

  const value = useMemo<ToastContextValue>(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {mounted &&
        createPortal(
          <div className={styles.toastViewport}>
            {toasts.map((entry) => (
              <Toast
                key={entry.id}
                message={entry.message}
                variant={entry.variant}
                actions={entry.actions}
                onClose={() => dismiss(entry.id)}
              />
            ))}
          </div>,
          document.body,
        )}
    </ToastContext.Provider>
  );
}
